/**
 * Skool Over Training - My Programs Page
 * View and follow assigned training programs
 * Interactive drill completion and proof upload
 */

import { useState, useEffect } from 'react';
import {
  listPrograms,
  getProgram,
  listWorkoutLogs,
  listSubmissions,
  type Organization,
  type Program,
  type Module,
  type DrillData,
  type WarmupExercise,
  type WorkoutLog,
  type DrillSubmission,
} from '@tss/shared';
import { BookOpen, CheckCircle, Clock, ChevronRight, Target, ChevronLeft, Dumbbell, Video, Upload, Play } from 'lucide-react';
import { ProofUploadModal } from '../components/ProofUploadModal';
import { WorkoutTracker } from '../components/WorkoutTracker';
import { PageHeader, EmptyState, Skeleton } from '../components/UI';

interface MyProgramsPageProps {
  org: Organization | null;
  proxyAthleteId?: string;
}

export function MyProgramsPage({ org, proxyAthleteId }: MyProgramsPageProps) {
  const [programs, setPrograms] = useState<Program[]>([]);
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [selectedModule, setSelectedModule] = useState<Module | null>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingModules, setLoadingModules] = useState(false);

  // Drill completion and proof tracking
  const [completedDrills, setCompletedDrills] = useState<Set<number>>(new Set());
  const [submissions, setSubmissions] = useState<Map<number, DrillSubmission>>(new Map());
  const [proofModal, setProofModal] = useState<{ drillIndex: number; drillName: string } | null>(null);

  // Workout tracker mode
  const [showWorkoutTracker, setShowWorkoutTracker] = useState(false);

  useEffect(() => {
    if (org) {
      loadPrograms();
      // Reset selections when proxyAthleteId changes
      setSelectedProgram(null);
      setSelectedModule(null);
    } else {
      setLoading(false);
    }
  }, [org, proxyAthleteId]);

  async function loadPrograms() {
    if (!org) return;
    setLoading(true);
    try {
      const data = await listPrograms(org.org_id);
      setPrograms(data.programs || []);
    } catch (err) {
      console.error('Failed to load programs:', err);
      setPrograms([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleSelectProgram(program: Program) {
    setSelectedProgram(program);
    setSelectedModule(null);
    setLoadingModules(true);
    // Reset drill tracking when changing programs
    setCompletedDrills(new Set());
    setSubmissions(new Map());
    try {
      const data = await getProgram(program.program_id);
      setModules(data.modules || []);
    } catch (err) {
      console.error('Failed to load modules:', err);
      setModules([]);
    } finally {
      setLoadingModules(false);
    }
  }

  async function handleSelectModule(module: Module) {
    setSelectedModule(module);
    // Reset and load drill completion/submission data for this module
    setCompletedDrills(new Set());
    setSubmissions(new Map());

    if (selectedProgram) {
      try {
        const [logsData, submissionsData] = await Promise.all([
          listWorkoutLogs(selectedProgram.program_id, { module_id: module.module_id, athlete_id: proxyAthleteId }).catch(() => ({ workout_logs: [] })),
          listSubmissions(selectedProgram.program_id, { module_id: module.module_id, user_id: proxyAthleteId }).catch(() => ({ submissions: [] })),
        ]);

        // Restore completed state from logs
        const completed = new Set<number>();
        (logsData.workout_logs || []).forEach((log: WorkoutLog) => {
          if (log.completed_at) {
            completed.add(log.drill_index);
          }
        });
        setCompletedDrills(completed);

        // Map submissions by drill index
        const submissionMap = new Map<number, DrillSubmission>();
        (submissionsData.submissions || []).forEach((sub: DrillSubmission) => {
          submissionMap.set(sub.drill_index, sub);
        });
        setSubmissions(submissionMap);
      } catch (err) {
        console.error('Failed to load workout data:', err);
      }
    }
  }

  function reloadModuleData() {
    if (selectedModule) {
      handleSelectModule(selectedModule);
    }
  }

  if (loading) {
    return (
      <div style={{ maxWidth: '900px', margin: '0 auto', width: '100%' }}>
        <Skeleton height={36} width="50%" style={{ marginBottom: 8 }} />
        <Skeleton height={20} width="70%" style={{ marginBottom: 32 }} />
        <Skeleton.Card count={3} />
      </div>
    );
  }

  // Show module detail view with drills
  if (selectedModule && selectedProgram) {
    const drills = selectedModule.drills || [];
    const warmup = selectedModule.warmup_exercises || [];
    const cooldown = selectedModule.cooldown_exercises || [];
    const moduleIndex = modules.findIndex(m => m.module_id === selectedModule.module_id) + 1;

    // Show the comprehensive WorkoutTracker
    if (showWorkoutTracker) {
      return (
        <div style={{ maxWidth: '900px', margin: '0 auto', width: '100%' }}>
          <WorkoutTracker
            programId={selectedProgram.program_id}
            module={selectedModule}
            orgId={selectedProgram.org_id}
            proxyAthleteId={proxyAthleteId}
            onComplete={() => {
              setShowWorkoutTracker(false);
              reloadModuleData();
            }}
            onClose={() => {
              setShowWorkoutTracker(false);
              reloadModuleData(); // Reload progress when closing
            }}
          />
        </div>
      );
    }

    return (
      <div style={{ maxWidth: '900px', margin: '0 auto', width: '100%' }}>
        {/* Back button */}
        <button
          onClick={() => setSelectedModule(null)}
          className="btn-ghost flex items-center gap-2"
          style={{ marginBottom: '16px', padding: '8px 12px', color: 'var(--gray-600)' }}
        >
          <ChevronLeft size={18} />
          Back to Modules
        </button>

        {/* Module header */}
        <div
          className="card animate-fade-in"
          style={{
            background: 'linear-gradient(135deg, var(--primary-start) 0%, var(--primary-end) 100%)',
            color: 'white',
            border: 'none',
            marginBottom: '20px',
            padding: 'clamp(16px, 4vw, 28px)'
          }}
        >
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 10px',
            background: 'rgba(59, 130, 246, 0.3)',
            borderRadius: '20px',
            fontSize: '11px',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '10px',
            color: 'var(--accent-light)'
          }}>
            <Dumbbell size={12} />
            Module {moduleIndex}
          </span>
          <h2 style={{ fontSize: 'clamp(18px, 5vw, 24px)', fontWeight: '700', marginBottom: '6px', color: 'white' }}>
            {selectedModule.title}
          </h2>
          {selectedModule.description && (
            <p style={{ opacity: 0.7, fontSize: '14px' }}>
              {selectedModule.description}
            </p>
          )}
          <p style={{ opacity: 0.6, fontSize: '13px', marginTop: '8px' }}>
            {selectedProgram.title}
          </p>

          {/* Start Workout Button */}
          {drills.length > 0 && (
            <button
              onClick={() => setShowWorkoutTracker(true)}
              style={{
                marginTop: '16px',
                padding: '12px 24px',
                background: 'rgba(255, 255, 255, 0.2)',
                border: '2px solid rgba(255, 255, 255, 0.4)',
                borderRadius: '12px',
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s ease',
              }}
            >
              <Play size={20} />
              Start Workout
            </button>
          )}
        </div>

        {/* Warmup section */}
        {warmup.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: 'var(--gray-500)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '12px'
            }}>
              Warm-up ({warmup.length})
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {warmup.map((exercise, index) => (
                <ExerciseCard key={`warmup-${index}`} exercise={exercise} />
              ))}
            </div>
          </div>
        )}

        {/* Main drills section */}
        {drills.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: 'var(--gray-500)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '12px'
            }}>
              Drills ({drills.length})
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {drills.map((drill, index) => {
                const drillIndex = warmup.length + index;
                const drillData = typeof drill === 'string' ? { name: drill } : drill;
                const submission = submissions.get(drillIndex);
                // Check both camelCase and snake_case for requires_proof
                const requiresProof = drillData.requires_proof || drillData.requiresProof;
                return (
                  <DrillCard
                    key={`drill-${index}`}
                    drill={drill}
                    index={index + 1}
                    isCompleted={completedDrills.has(drillIndex)}
                    submission={submission}
                    onUploadProof={requiresProof ? () => setProofModal({ drillIndex, drillName: drillData.name }) : undefined}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Cooldown section */}
        {cooldown.length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h3 style={{
              fontSize: '14px',
              fontWeight: '600',
              color: 'var(--gray-500)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '12px'
            }}>
              Cool-down ({cooldown.length})
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {cooldown.map((exercise, index) => (
                <ExerciseCard key={`cooldown-${index}`} exercise={exercise} />
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {drills.length === 0 && warmup.length === 0 && cooldown.length === 0 && (
          <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
            <p style={{ color: 'var(--gray-500)' }}>No exercises in this module yet.</p>
          </div>
        )}

        {/* Proof Upload Modal */}
        {proofModal && selectedProgram && (
          <ProofUploadModal
            programId={selectedProgram.program_id}
            moduleId={selectedModule.module_id}
            drillIndex={proofModal.drillIndex}
            drillName={proofModal.drillName}
            orgId={selectedProgram.org_id}
            proxyAthleteId={proxyAthleteId}
            onClose={() => setProofModal(null)}
            onSubmitted={() => {
              setProofModal(null);
              reloadModuleData();
            }}
          />
        )}
      </div>
    );
  }

  // Show program detail view
  if (selectedProgram) {
    return (
      <div style={{ maxWidth: '900px', margin: '0 auto', width: '100%' }}>
        {/* Back button */}
        <button
          onClick={() => setSelectedProgram(null)}
          className="btn-ghost flex items-center gap-2"
          style={{ marginBottom: '16px', padding: '8px 12px', color: 'var(--gray-600)' }}
        >
          <ChevronLeft size={18} />
          Back to Programs
        </button>

        {/* Program header */}
        <div
          className="card animate-fade-in"
          style={{
            background: 'linear-gradient(135deg, var(--primary-start) 0%, var(--primary-end) 100%)',
            color: 'white',
            border: 'none',
            marginBottom: '20px',
            padding: 'clamp(16px, 4vw, 28px)'
          }}
        >
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 10px',
            background: 'rgba(59, 130, 246, 0.3)',
            borderRadius: '20px',
            fontSize: '11px',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '10px',
            color: 'var(--accent-light)'
          }}>
            <Target size={12} />
            {selectedProgram.status}
          </span>
          <h2 style={{ fontSize: 'clamp(18px, 5vw, 24px)', fontWeight: '700', marginBottom: '6px', color: 'white' }}>
            {selectedProgram.title}
          </h2>
          {selectedProgram.description && (
            <p style={{ opacity: 0.7, fontSize: '14px' }}>
              {selectedProgram.description}
            </p>
          )}
        </div>

        {/* Modules list */}
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--gray-800)', marginBottom: '16px' }}>
          Modules ({modules.length})
        </h3>

        {loadingModules ? (
          <Skeleton.Card count={3} />
        ) : modules.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {modules.map((module, index) => (
              <ModuleCard
                key={module.module_id}
                module={module}
                index={index + 1}
                onClick={() => handleSelectModule(module)}
              />
            ))}
          </div>
        ) : (
          <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
            <p style={{ color: 'var(--gray-500)' }}>No modules in this program yet.</p>
          </div>
        )}
      </div>
    );
  }

  // Show programs list
  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', width: '100%' }}>
      {/* Header */}
      <PageHeader
        title="My Programs"
        description={programs.length > 0
          ? 'Your assigned training programs'
          : 'Programs assigned by your coach will appear here'}
        icon={BookOpen}
        iconColor="var(--accent)"
      />

      {programs.length === 0 ? (
        <div className="card">
          <EmptyState.Programs />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {programs.map((program) => (
            <ProgramCard
              key={program.program_id}
              program={program}
              onClick={() => handleSelectProgram(program)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ModuleCard({ module, index, onClick }: { module: Module; index: number; onClick: () => void }) {
  const drillCount = (module.drills || []).length;
  const warmupCount = (module.warmup_exercises || []).length;
  const cooldownCount = (module.cooldown_exercises || []).length;
  const totalExercises = drillCount + warmupCount + cooldownCount;

  return (
    <div className="card card-interactive" style={{ padding: 'clamp(12px, 3vw, 16px)' }} onClick={onClick}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div
          className="flex-center"
          style={{
            width: 'clamp(32px, 10vw, 40px)',
            height: 'clamp(32px, 10vw, 40px)',
            background: 'linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%)',
            borderRadius: '10px',
            color: 'white',
            fontWeight: '700',
            fontSize: 'clamp(14px, 4vw, 16px)',
            flexShrink: 0
          }}
        >
          {index}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h4 className="truncate" style={{ fontSize: 'clamp(14px, 4vw, 15px)', fontWeight: '600', color: 'var(--gray-900)' }}>
            {module.title}
          </h4>
          {module.description && (
            <p className="truncate" style={{ fontSize: '13px', color: 'var(--gray-500)', marginTop: '2px' }}>
              {module.description}
            </p>
          )}
          {totalExercises > 0 && (
            <p style={{ fontSize: '12px', color: 'var(--gray-400)', marginTop: '4px' }}>
              {totalExercises} exercise{totalExercises !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        <ChevronRight size={20} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
      </div>
    </div>
  );
}

function ProgramCard({ program, onClick }: { program: Program; onClick: () => void }) {
  const statusConfig: Record<string, { label: string; bg: string; color: string }> = {
    PUBLISHED: {
      label: 'Active',
      bg: 'rgba(249, 115, 22, 0.1)',
      color: 'var(--success)'
    },
    DRAFT: {
      label: 'Draft',
      bg: 'var(--gray-100)',
      color: 'var(--gray-600)'
    },
  };

  const config = statusConfig[program.status] || statusConfig.PUBLISHED;

  return (
    <div className="card card-interactive" style={{ padding: 'clamp(12px, 3vw, 20px)' }} onClick={onClick}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div
          className="icon-container icon-container-md"
          style={{
            background: 'linear-gradient(135deg, var(--accent) 0%, var(--accent-dark) 100%)',
            flexShrink: 0
          }}
        >
          <BookOpen size={20} style={{ color: 'white' }} />
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '2px' }}>
            <h3 className="truncate" style={{ fontSize: 'clamp(14px, 4vw, 16px)', fontWeight: '600', color: 'var(--gray-900)' }}>
              {program.title}
            </h3>
            <span style={{
              padding: '2px 8px',
              background: config.bg,
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: '600',
              color: config.color,
              whiteSpace: 'nowrap'
            }}>
              {config.label}
            </span>
          </div>
          {program.description && (
            <p className="truncate" style={{ fontSize: '13px', color: 'var(--gray-500)' }}>
              {program.description}
            </p>
          )}
        </div>
        <ChevronRight size={18} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
      </div>
    </div>
  );
}

function DrillCard({
  drill,
  index,
  isCompleted,
  submission,
  onUploadProof,
}: {
  drill: string | DrillData;
  index: number;
  isCompleted?: boolean;
  submission?: DrillSubmission;
  onUploadProof?: () => void;
}) {
  // Handle both string and DrillData types
  const drillData = typeof drill === 'string' ? { name: drill } : drill;
  // Check both camelCase and snake_case for requires_proof
  const requiresProof = drillData.requires_proof || drillData.requiresProof;
  const hasSubmission = !!submission;
  const submissionStatus = submission?.status;

  return (
    <div
      className="card"
      style={{
        padding: 'clamp(12px, 3vw, 16px)',
        background: isCompleted ? 'rgba(249, 115, 22, 0.05)' : 'white',
        border: isCompleted ? '1px solid rgba(249, 115, 22, 0.2)' : '1px solid var(--gray-100)',
        transition: 'all 0.2s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        {/* Index indicator */}
        <div
          style={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            background: isCompleted ? 'var(--success)' : 'var(--accent)',
            color: 'white',
            fontWeight: '600',
            fontSize: '14px',
          }}
        >
          {isCompleted ? (
            <CheckCircle size={18} />
          ) : (
            index
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h4
            style={{
              fontSize: '15px',
              fontWeight: '600',
              color: isCompleted ? 'var(--gray-500)' : 'var(--gray-900)',
              textDecoration: isCompleted ? 'line-through' : 'none',
              marginBottom: '4px',
            }}
          >
            {drillData.name}
          </h4>
          {drillData.description && (
            <p style={{ fontSize: '13px', color: 'var(--gray-600)', marginBottom: '8px' }}>
              {drillData.description}
            </p>
          )}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            {drillData.duration_seconds && (
              <span style={{ fontSize: '12px', color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Clock size={14} />
                {Math.floor(drillData.duration_seconds / 60)}:{(drillData.duration_seconds % 60).toString().padStart(2, '0')}
              </span>
            )}
            {drillData.reps && drillData.sets && (
              <span style={{ fontSize: '12px', color: 'var(--gray-500)' }}>
                {drillData.sets} sets x {drillData.reps} reps
              </span>
            )}
            {drillData.reps && !drillData.sets && (
              <span style={{ fontSize: '12px', color: 'var(--gray-500)' }}>
                {drillData.reps} reps
              </span>
            )}
            {drillData.difficulty && (
              <span style={{
                fontSize: '11px',
                fontWeight: '500',
                padding: '2px 8px',
                borderRadius: '12px',
                background: drillData.difficulty === 'beginner' ? 'rgba(249, 115, 22, 0.1)' :
                           drillData.difficulty === 'intermediate' ? 'rgba(251, 191, 36, 0.1)' :
                           'rgba(239, 68, 68, 0.1)',
                color: drillData.difficulty === 'beginner' ? 'var(--success)' :
                       drillData.difficulty === 'intermediate' ? '#d97706' :
                       '#dc2626',
                textTransform: 'capitalize'
              }}>
                {drillData.difficulty}
              </span>
            )}
            {/* Proof status/button */}
            {requiresProof && (
              hasSubmission ? (
                <span style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: submissionStatus === 'APPROVED' ? 'var(--success)' :
                         submissionStatus === 'NEEDS_WORK' ? 'var(--warning)' : 'var(--accent)',
                  background: submissionStatus === 'APPROVED' ? 'rgba(249, 115, 22, 0.1)' :
                              submissionStatus === 'NEEDS_WORK' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}>
                  <CheckCircle size={12} />
                  {submissionStatus === 'APPROVED' ? 'Approved' :
                   submissionStatus === 'NEEDS_WORK' ? 'Needs Work' : 'Proof Submitted'}
                </span>
              ) : onUploadProof ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onUploadProof();
                  }}
                  style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: 'white',
                    background: 'var(--accent)',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: 'none',
                  }}
                >
                  <Upload size={12} />
                  Upload Proof
                </button>
              ) : (
                <span style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: 'var(--accent)',
                  background: 'rgba(59, 130, 246, 0.1)',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  <Video size={12} />
                  Video required
                </span>
              )
            )}
          </div>
          {drillData.skill_tags && drillData.skill_tags.length > 0 && (
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
              {drillData.skill_tags.map((tag, i) => (
                <span
                  key={i}
                  style={{
                    fontSize: '11px',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    background: 'var(--gray-100)',
                    color: 'var(--gray-600)'
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ExerciseCard({ exercise }: { exercise: WarmupExercise }) {
  return (
    <div className="card" style={{ padding: 'clamp(10px, 3vw, 14px) clamp(12px, 3vw, 16px)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', flexWrap: 'wrap' }}>
        <div
          className="flex-center"
          style={{
            width: '28px',
            height: '28px',
            background: 'var(--gray-100)',
            borderRadius: '6px',
            flexShrink: 0
          }}
        >
          <Dumbbell size={14} style={{ color: 'var(--gray-500)' }} />
        </div>
        <div style={{ flex: 1, minWidth: '120px' }}>
          <h4 style={{ fontSize: '14px', fontWeight: '500', color: 'var(--gray-800)' }}>
            {exercise.name}
          </h4>
          {exercise.description && (
            <p style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '2px' }}>
              {exercise.description}
            </p>
          )}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '4px' }}>
            {exercise.duration_seconds && (
              <span style={{ fontSize: '11px', color: 'var(--gray-500)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Clock size={12} />
                {Math.floor(exercise.duration_seconds / 60)}:{(exercise.duration_seconds % 60).toString().padStart(2, '0')}
              </span>
            )}
            {exercise.reps && (
              <span style={{ fontSize: '11px', color: 'var(--gray-500)' }}>
                {exercise.reps} reps
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
